import mediaManagerRepository from 'modules/manager/domain/repositories/media-manager-repository';
import {
  ExistingTemplateRef,
  SkippedTemplateEntry,
  TemplateEntryProblem,
  TemplateFileEntry,
  TemplateFileError,
  findNameConflicts,
  parseTemplateFile,
  rebaseAssetUrls,
  resolveImportName,
  toTemplateOverwrite,
} from 'modules/manager/domain/template-file';
import { isTemplateNameConflict, normalizeTemplateName } from 'modules/manager/domain/template-name-conflict';
import {
  MEDIA_MANAGER_PRESETS_LIST_TAG,
  MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_LIST_TAG,
  MEDIA_MANAGER_TEMPLATES_LIST_TAG,
  MEDIA_MANAGER_TEMPLATE_GROUPS_LIST_TAG,
  MEDIA_MANAGER_TEMPLATE_GROUP_TAG,
  MEDIA_MANAGER_TEMPLATE_TAG,
  mediaManagerApi,
} from 'modules/manager/store/media-manager-api';
import React, { useCallback, useRef, useState } from 'react';
import { readFileAsText } from 'shared/utils/read-file-as-text';
import { useAppDispatch } from 'store/hooks';

/** Where the file goes: a specific template group. */
export type ImportTarget = { templateGroupId: number; isPreset?: boolean };

export type ImportSkipReason =
  | TemplateEntryProblem
  /** Presets: a preset with this name already exists. */
  | 'name-taken'
  /** Organization: every «(импорт N)» we tried was rejected by the server. */
  | 'no-free-name'
  | 'request-failed';

export interface ImportedTemplate {
  id: number;
  /** Name it was created under — differs from `sourceName` when a suffix was added. */
  name: string;
  sourceName: string;
}

export interface ImportSkippedTemplate {
  /** Position in the file, zero-based — the result lists entries in file order. */
  index: number;
  name?: string;
  reason: ImportSkipReason;
}

export type ImportTemplatesResult =
  | { status: 'rejected'; fileName: string; error: TemplateFileError | 'read-failed' }
  | {
      status: 'done';
      fileName: string;
      total: number;
      created: ImportedTemplate[];
      /** Existing templates whose content was replaced from the file. */
      overwritten: ImportedTemplate[];
      skipped: ImportSkippedTemplate[];
    };

/** A file entry whose name is already taken in the target — the user decides its fate. */
export interface ImportConflict {
  /** Identity of the entry within the pending import — pass it back to `confirmOverwrite`. */
  entryIndex: number;
  /** Name in the file. */
  name: string;
  /** Name of the template it would overwrite. */
  existingName: string;
  /**
   * What happens without overwriting: the name it is created under
   * (organization) or `null` — the entry is skipped (presets).
   */
  fallbackName: string | null;
}

export interface PendingImport {
  fileName: string;
  isPreset: boolean;
  conflicts: ImportConflict[];
}

interface Args {
  /** `null` while there is nowhere to import to (no active organization). */
  target: ImportTarget | null;
  /** Templates already in the target list — the page's cache. */
  existing: readonly ExistingTemplateRef[];
  /** Size of the target list: new templates go after it. */
  templatesCount: number;
}

export interface UseImportTemplatesResult {
  isImporting: boolean;
  /** Outcome of the last import, until `clearResult`. */
  result: ImportTemplatesResult | null;
  clearResult: () => void;
  /** Set while the file has same-named templates and waits for the user's choice. */
  pending: PendingImport | null;
  /** Runs the pending import, overwriting the chosen entries (by `entryIndex`). */
  confirmOverwrite: (entryIndexes: readonly number[]) => Promise<ImportTemplatesResult | null>;
  /** Drops the pending import — nothing is created or changed. */
  cancelImport: () => void;
  /** Opens the file picker. */
  pickFile: () => void;
  /** Resolves `null` when nothing ran yet: busy, no target, or waiting for `confirmOverwrite`. */
  importFile: (file: File) => Promise<ImportTemplatesResult | null>;
  /** Hidden `<input type="file">` — render it anywhere on the page. */
  fileInput: React.ReactElement;
}

/** How many times a name is retried after the server said it is taken. */
const MAX_NAME_ATTEMPTS = 5;

interface ParsedImport {
  fileName: string;
  target: ImportTarget;
  entries: TemplateFileEntry[];
  /** Position in the file of each entry. */
  indexes: number[];
  total: number;
  skipped: SkippedTemplateEntry[];
  conflicts: (ImportConflict & { existingId: number })[];
}

function entryIndexes(total: number, skipped: readonly { index: number }[]): number[] {
  const skippedIndexes = new Set(skipped.map((s) => s.index));
  return Array.from({ length: total }, (_, i) => i).filter((i) => !skippedIndexes.has(i));
}

/**
 * Imports a template file into the current organization or into the presets.
 *
 * Entries go one by one through the repository and the list is refetched once at
 * the end: RTK Query's create mutation invalidates the list on every call (N
 * creates = N refetches), and the names and `order` must stay deterministic,
 * which a parallel run cannot promise.
 *
 * When names in the file are already taken, the import stops at `pending` and
 * waits for one decision on all of them (`confirmOverwrite` / `cancelImport`).
 * A chosen entry replaces the existing template's content; the rest keep the
 * default: in an organization the template is created as «… (импорт)», a preset
 * with the same name is skipped — presets are seen by every organization, and a
 * library re-imported after each edit on the test stand would fill up with
 * suffixes.
 */
export function useImportTemplates({ target, existing, templatesCount }: Args): UseImportTemplatesResult {
  const dispatch = useAppDispatch();
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState<ImportTemplatesResult | null>(null);
  const [pending, setPending] = useState<PendingImport | null>(null);
  const pendingRef = useRef<ParsedImport | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Synchronous guard, as in `use-duplicate-template.ts`.
  const inFlight = useRef(false);

  const runImport = useCallback(
    async (parsed: ParsedImport, overwrite: ReadonlyMap<number, number>): Promise<ImportTemplatesResult> => {
      const isPreset = parsed.target.isPreset === true;
      const taken = new Set(existing.map((template) => normalizeTemplateName(template.name)));
      const created: ImportedTemplate[] = [];
      const overwritten: ImportedTemplate[] = [];
      const skipped: ImportSkippedTemplate[] = [];
      let order = templatesCount;

      for (let i = 0; i < parsed.entries.length; i += 1) {
        const entry = parsed.entries[i];
        const index = parsed.indexes[i];
        const objects = rebaseAssetUrls(entry.objects, window.location.origin);

        const overwriteId = overwrite.get(i);
        if (overwriteId !== undefined) {
          try {
            const template = await mediaManagerRepository.updateTemplate(toTemplateOverwrite({ ...entry, objects }, overwriteId));
            overwritten.push({ id: template.id, name: template.name, sourceName: entry.name });
          } catch (error) {
            console.error('Failed to overwrite media manager template:', error);
            skipped.push({ index, name: entry.name, reason: 'request-failed' });
          }
          continue;
        }

        let outcome: ImportSkipReason | null = isPreset ? 'name-taken' : 'no-free-name';
        for (let attempt = 0; attempt < MAX_NAME_ATTEMPTS; attempt += 1) {
          const name = resolveImportName(entry.name, taken, isPreset ? 'skip' : 'suffix');
          if (name === null) break;
          try {
            const template = await mediaManagerRepository.createTemplate({
              name,
              objects,
              version: entry.version,
              templateGroupId: parsed.target.templateGroupId,
              sportType: entry.sportType,
              width: entry.width,
              height: entry.height,
              order,
              templateType: entry.templateType,
              templateSubtypes: entry.templateSubtypes,
              matchLayout: entry.matchLayout,
              playerRanking: entry.playerRanking,
            });
            taken.add(normalizeTemplateName(name));
            order += 1;
            created.push({ id: template.id, name: template.name, sourceName: entry.name });
            outcome = null;
          } catch (error) {
            if (isTemplateNameConflict(error)) {
              // The cache was stale (another tab took the name) — move past it.
              taken.add(normalizeTemplateName(name));
              if (!isPreset) continue;
            } else {
              console.error('Failed to import media manager template:', error);
              outcome = 'request-failed';
            }
          }
          break;
        }
        if (outcome) skipped.push({ index, name: entry.name, reason: outcome });
      }

      if (created.length > 0 || overwritten.length > 0) {
        // One dispatch: the list, plus every overwritten template — its full read
        // (editor, export) would otherwise be served stale from the cache.
        dispatch(
          mediaManagerApi.util.invalidateTags([
            isPreset ? MEDIA_MANAGER_PRESETS_LIST_TAG : MEDIA_MANAGER_TEMPLATES_LIST_TAG,
            isPreset ? MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_LIST_TAG : MEDIA_MANAGER_TEMPLATE_GROUPS_LIST_TAG,
            { type: MEDIA_MANAGER_TEMPLATE_GROUP_TAG, id: parsed.target.templateGroupId },
            ...overwritten.map((template) => ({ type: MEDIA_MANAGER_TEMPLATE_TAG, id: template.id })),
          ]),
        );
      }

      const done: ImportTemplatesResult = {
        status: 'done',
        fileName: parsed.fileName,
        total: parsed.total,
        created,
        overwritten,
        skipped: [...parsed.skipped, ...skipped].sort((a, b) => a.index - b.index),
      };
      setResult(done);
      return done;
    },
    [existing, templatesCount, dispatch],
  );

  const importFile = useCallback(
    async (file: File): Promise<ImportTemplatesResult | null> => {
      if (inFlight.current || !target) return null;

      inFlight.current = true;
      setIsImporting(true);
      try {
        let text: string;
        try {
          text = await readFileAsText(file);
        } catch (error) {
          console.error('Failed to read a template file:', error);
          const rejected: ImportTemplatesResult = { status: 'rejected', fileName: file.name, error: 'read-failed' };
          setResult(rejected);
          return rejected;
        }

        const parsed = parseTemplateFile(text);
        if ('error' in parsed) {
          const rejected: ImportTemplatesResult = { status: 'rejected', fileName: file.name, error: parsed.error };
          setResult(rejected);
          return rejected;
        }

        const isPreset = target.isPreset === true;
        const taken = new Set(existing.map((template) => normalizeTemplateName(template.name)));
        const total = parsed.entries.length + parsed.skipped.length;
        const prepared: ParsedImport = {
          fileName: file.name,
          target,
          entries: parsed.entries,
          indexes: entryIndexes(total, parsed.skipped),
          total,
          skipped: parsed.skipped,
          conflicts: findNameConflicts(parsed.entries, existing).map((conflict) => ({
            ...conflict,
            name: parsed.entries[conflict.entryIndex].name,
            fallbackName: resolveImportName(parsed.entries[conflict.entryIndex].name, taken, isPreset ? 'skip' : 'suffix'),
          })),
        };

        if (prepared.conflicts.length > 0) {
          pendingRef.current = prepared;
          setPending({
            fileName: prepared.fileName,
            isPreset,
            conflicts: prepared.conflicts.map(({ entryIndex, name, existingName, fallbackName }) => ({
              entryIndex,
              name,
              existingName,
              fallbackName,
            })),
          });
          return null;
        }

        return await runImport(prepared, new Map());
      } finally {
        inFlight.current = false;
        setIsImporting(false);
      }
    },
    [target, existing, runImport],
  );

  const confirmOverwrite = useCallback(
    async (selected: readonly number[]): Promise<ImportTemplatesResult | null> => {
      const prepared = pendingRef.current;
      if (!prepared || inFlight.current) return null;

      pendingRef.current = null;
      setPending(null);
      inFlight.current = true;
      setIsImporting(true);
      try {
        const chosen = new Set(selected);
        const overwrite = new Map(
          prepared.conflicts
            .filter((conflict) => chosen.has(conflict.entryIndex))
            .map((conflict) => [conflict.entryIndex, conflict.existingId]),
        );
        return await runImport(prepared, overwrite);
      } finally {
        inFlight.current = false;
        setIsImporting(false);
      }
    },
    [runImport],
  );

  const cancelImport = useCallback(() => {
    pendingRef.current = null;
    setPending(null);
  }, []);

  const pickFile = useCallback(() => inputRef.current?.click(), []);
  const clearResult = useCallback(() => setResult(null), []);

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept=".json,application/json"
      hidden
      data-testid="template-file-input"
      onChange={(event) => {
        const file = event.target.files?.[0];
        // Reset so picking the same file again still fires `change`.
        event.target.value = '';
        if (file) void importFile(file);
      }}
    />
  );

  return { isImporting, result, clearResult, pending, confirmOverwrite, cancelImport, pickFile, importFile, fileInput };
}
